import { Tabs } from '@ark-ui/react';
import { css } from '@styled-system/css';
import type { FC, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import amplitude from '@/amplitude';
import type { AnalysisPageTabValue } from '@/features/analysisPage/types';

interface AnalysisPageTabsProps {
  children: ReactNode;
}

export const AnalysisPageTabs: FC<AnalysisPageTabsProps> = ({ children }) => {
  const navigate = useNavigate();
  const { id: analysisId, tab } = useParams<{ id: string; tab?: string }>();
  const { t } = useTranslation();

  // Détecter le type d'analyse depuis l'URL
  const analysisType = window.location.pathname.split('/')[2]; // 'products', 'vendors', ou 'public'

  // Déterminer l'onglet actif depuis les paramètres d'URL
  const activeTab: AnalysisPageTabValue = (() => {
    if (tab === 'niches') return 'niches';
    if (tab === 'history') return 'history';
    return 'statistics'; // Défaut
  })();

  // Navigation vers un nouvel onglet
  const handleTabChange = (newTab: AnalysisPageTabValue) => {
    const tabRoutes = {
      statistics: 'statistics',
      niches: 'niches',
      history: 'history',
      sellers: 'sellers',
    };

    // Tracker l'événement de changement d'onglet avec Amplitude
    if (newTab === 'statistics') {
      amplitude.track('Statistiques Clicked', { analysisId });
    } else if (newTab === 'niches') {
      amplitude.track('Niches Clicked', { analysisId });
    } else if (newTab === 'history') {
      amplitude.track('Historique Clicked', { analysisId });
    }

    navigate(`/analysis/${analysisType}/${analysisId}/tab/${tabRoutes[newTab]}`);
  };
  const tabsRootStyles = css({
    w: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // Important pour que flex fonctionne correctement avec overflow
  });

  const tabsListStyles = css({
    display: 'flex',
    alignItems: 'stretch',
    bg: 'transparent',
    px: '1rem', // 16px padding horizontal
    gap: '2rem', // Espacement entre tabs
  });

  const tabTriggerStyles = css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.0625rem', // 1px gap entre texte et ligne
    px: 0,
    py: '0.75rem', // 12px padding vertical
    fontSize: 'md', // 13px (font-md du design)
    fontWeight: 500, // Manrope Medium par défaut
    fontFamily: 'sans',
    color: 'font.muted', // font-muted du design (état inactif)
    bg: 'transparent',
    border: 'none',
    cursor: 'pointer',

    // Prendre l'espace de la ligne de soulignement
    // Pour éviter l'effet de "saut" lors du changement de tab
    '&::after': {
      content: '""',
      w: '100%',
      h: '1px',
      bg: 'transparent', // Ligne transparente par défaut
      display: 'block',
    },

    // État actif
    _selected: {
      color: 'font.primary', // font-primary du design (état actif)
      transformOrigin: 'left bottom',
      transform: 'scale(1.05)', // Légère mise en avant

      // Ligne de soulignement pour l'état actif
      '&::after': {
        content: '""',
        w: '100%',
        h: '1px',
        bg: 'font.primary', // Ligne blanche comme dans le design
        borderRadius: '99px',
        display: 'block',
      },
    },

    // État hover
    _hover: {
      color: 'font.muted', // Couleur intermédiaire au hover
    },

    // Focus states
    _focus: {
      outline: 'none',
    },
  });

  const tabsContentStyles = css({
    flex: 1,
    overflowY: 'auto',
    bg: 'transparent',
    minHeight: 0, // Important pour que flex fonctionne correctement avec overflow
  });

  const tabContentStyles = css({
    w: '100%',
    minHeight: '100%',
    p: '1rem', // 16px padding
    pb: '4rem',
  });

  const tabs = [
    { value: 'statistics' as const, label: t('analysisDetail.tabs.statistics') },
    { value: 'niches' as const, label: t('analysisDetail.tabs.niches') },
    { value: 'history' as const, label: t('analysisDetail.tabs.history') },
  ];

  return (
    <Tabs.Root
      value={activeTab}
      onValueChange={(details) => handleTabChange(details.value as AnalysisPageTabValue)}
      className={tabsRootStyles}
    >
      <Tabs.List className={tabsListStyles}>
        {tabs.map((tab) => (
          <Tabs.Trigger key={tab.value} value={tab.value} className={tabTriggerStyles}>
            {tab.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>

      <div className={tabsContentStyles}>
        <Tabs.Content value="statistics" className={tabContentStyles}>
          {activeTab === 'statistics' && children}
        </Tabs.Content>
        <Tabs.Content value="niches" className={tabContentStyles}>
          {activeTab === 'niches' && children}
        </Tabs.Content>
        <Tabs.Content value="history" className={tabContentStyles}>
          {activeTab === 'history' && children}
        </Tabs.Content>
      </div>
    </Tabs.Root>
  );
};
